import { useState, useEffect, useCallback } from 'react';
import { executiveAPI } from '../services/api';
import { fmt } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import CarteraTab from '../components/executive/CarteraTab';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const ANIO_ACTUAL = new Date().getFullYear();

// Fechas armadas a mano para que no intervenga la zona horaria.
function iso(d) {
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}
const finDeMes = (y, m) => new Date(y, m + 1, 0);

function rangoPreset(id) {
  const h = new Date();
  const y = h.getFullYear(), m = h.getMonth();
  switch (id) {
    case 'mes':       return [iso(new Date(y, m, 1)), iso(finDeMes(y, m))];
    case 'mes_ant':   return [iso(new Date(y, m - 1, 1)), iso(finDeMes(y, m - 1))];
    case 'trim': {
      const q = Math.floor(m / 3) * 3;
      return [iso(new Date(y, q, 1)), iso(finDeMes(y, q + 2))];
    }
    case 'trim_ant': {
      const q = Math.floor(m / 3) * 3 - 3;
      return [iso(new Date(y, q, 1)), iso(finDeMes(y, q + 2))];
    }
    case 'ytd':       return [y + '-01-01', iso(h)];
    case 'anio_ant':  return [(y - 1) + '-01-01', (y - 1) + '-12-31'];
    case 'anio':
    default:          return [y + '-01-01', y + '-12-31'];
  }
}

// Mismo dia y mes, n anios atras. El 29-feb cae al 28, que es lo que queremos.
function menosAnios(isoStr, n) {
  const p = String(isoStr).split('-');
  if (p.length !== 3) return isoStr;
  const y = parseInt(p[0], 10) - n;
  const m = parseInt(p[1], 10) - 1;
  const d = parseInt(p[2], 10);
  const ultimo = new Date(y, m + 1, 0).getDate();
  return iso(new Date(y, m, Math.min(d, ultimo)));
}

const PRESETS = [
  ['mes',      'Mes actual'],
  ['mes_ant',  'Mes anterior'],
  ['trim',     'Trimestre'],
  ['trim_ant', 'Trim. anterior'],
  ['ytd',      'Ano a la fecha'],
  ['anio',     'Ano completo'],
  ['anio_ant', 'Ano anterior'],
];

const BUCKETS = {
  invoiced:        'Facturas emitidas',
  refunds:         'Notas de credito',
  accrual:         'Provision de devengo',
  billings_excess: 'Ajuste de obra (RMD)',
};

const FAM_LABEL = {
  FIERRO: 'Fierro',
  ALUMINIO: 'Aluminio',
  NEUTRAL: 'Neutral (labor / servicios)',
  NO_INGRESO: 'No ingreso',
  'SIN CLASIFICAR': 'Sin clasificar',
};

const FAM_COLOR = {
  FIERRO: 'var(--bronze)',
  ALUMINIO: 'var(--blue)',
  NEUTRAL: 'var(--text-3)',
  NO_INGRESO: 'var(--text-3)',
  'SIN CLASIFICAR': 'var(--red)',
};

// Los renglones del desglose que NO son una persona. Se pintan en gris para que
// no se confundan con un vendedor, pero SI cuentan en la suma: si se escondieran,
// el desglose no cuadraria contra el total del mes y eso es peor.
const NO_ES_PERSONA = ['VARIOS', 'SIN_PEDIDO', 'NOTA_DE_CREDITO', 'SIN_VENDEDOR_EN_ODOO'];

function dinero(v) {
  const n = Number(v) || 0;
  const s = fmt.currency(Math.abs(n));
  return n < 0 ? '-' + s : s;
}

function mesCorto(key) {
  const p = String(key || '').split('-');
  if (p.length !== 2) return key;
  const i = parseInt(p[1], 10) - 1;
  return (MESES[i] || p[1]) + ' ' + p[0].slice(2);
}

function horaLocal(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return iso; }
}

export default function ExecutivePage() {
  const [tab, setTab]         = useState('ingreso');
  const [desde, setDesde]     = useState(ANIO_ACTUAL + '-01-01');
  const [hasta, setHasta]     = useState(ANIO_ACTUAL + '-12-31');
  const [data, setData]       = useState(null);
  const [fam, setFam]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [drill, setDrill]     = useState(null);
  const [comp, setComp]       = useState(null);
  const [compLoad, setCompLoad] = useState(false);

  // Filtro de vendedor DENTRO del panel de detalle. Filtra la tabla de abajo,
  // no la pagina: las tarjetas y las graficas de arriba siguen siendo de toda
  // la empresa, a proposito. El vendedor que se muestra sale del pedido de Odoo
  // y no es el que cobra la comision, asi que no puede ser el numero oficial
  // de la pantalla.
  const [vendFiltro, setVendFiltro] = useState('');

  const cargar = useCallback((d, h) => {
    if (!d || !h || d > h) { setError('El rango de fechas no es valido'); return; }
    setLoading(true);
    setError(null);
    setDrill(null);
    Promise.all([
      executiveAPI.revenueMonthly(d, h),
      executiveAPI.revenueByFamily(d, h),
    ])
      .then(([m, f]) => { setData(m.data); setFam(f.data); })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(desde, hasta); }, [desde, hasta, cargar]);

  useEffect(() => {
    if (!drill) return;
    const el = document.getElementById('drill-panel');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [drill]);

  // El comparativo se carga solo al abrir su pestana: son 6 consultas a Odoo.
  useEffect(() => {
    if (tab !== 'comparativo') return;
    setCompLoad(true);
    const anios = [0, 1, 2];
    Promise.all(anios.flatMap((n) => {
      const d = menosAnios(desde, n), h = menosAnios(hasta, n);
      const y = d.slice(0, 4);
      return [
        executiveAPI.revenueByFamily(d, h),
        executiveAPI.revenueByFamily(y + '-01-01', h),
      ];
    }))
      .then((res) => {
        const filas = anios.map((n, i) => {
          const per = res[i * 2].data, ytd = res[i * 2 + 1].data;
          const de = (r, f) => (r.familias.find((x) => x.familia === f) || {}).total || 0;
          return {
            anio: menosAnios(desde, n).slice(0, 4),
            desde: menosAnios(desde, n),
            hasta: menosAnios(hasta, n),
            total: per.total,
            fierro: de(per, 'FIERRO'),
            aluminio: de(per, 'ALUMINIO'),
            neutral: de(per, 'NEUTRAL'),
            pct_alu: per.mezcla_material ? per.mezcla_material.pct_aluminio : 0,
            ytd: ytd.total,
          };
        });
        setComp(filas);
        setError(null);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setCompLoad(false));
  }, [tab, desde, hasta]);

  const actualizar = () => { executiveAPI.refresh().finally(() => cargar(desde, hasta)); };

  const aplicarPreset = (id) => {
    const [d, h] = rangoPreset(id);
    setDesde(d); setHasta(h);
  };

  const abrirDom = (etiqueta, dom) => {
    if (!dom || !dom.length) return;
    setVendFiltro('');
    setDrill({ etiqueta, loading: true, lines: [], total: 0, cargados: 0, porVendedor: null, error: null });
    executiveAPI.revenueDrilldown(dom, 200)
      .then((r) => setDrill({
        etiqueta, loading: false,
        lines: r.data.lines || [],
        total: r.data.total_lines || 0,
        // Cuantos renglones llegaron de verdad. Con esto la pantalla puede
        // decir "mostrando 200 de 1,340" en lugar de solo "1,340 apuntes",
        // que es lo que hacia creer que la tabla era todo el mes.
        cargados: r.data.returned || (r.data.lines || []).length,
        // El desglose viene calculado en el servidor sobre el periodo COMPLETO.
        // No se suma aqui: la consulta de apuntes ordena por fecha descendente
        // y trae solo los primeros, asi que un total armado con lo cargado
        // saldria sesgado a favor de quien facturo al cierre del mes.
        porVendedor: r.data.por_vendedor || null,
        error: null,
      }))
      .catch((e) => setDrill({
        etiqueta, loading: false, lines: [], total: 0, cargados: 0, porVendedor: null,
        error: e.response?.data?.error || e.message,
      }));
  };

  const abrirDetalle = (mes, bucket) =>
    abrirDom((BUCKETS[bucket] || bucket) + ' - ' + mesCorto(mes.month), mes.drill && mes.drill[bucket]);

  const meses  = data?.months || [];
  const totals = data?.totals || {};
  const familias = fam?.familias || [];
  const mezcla   = fam?.mezcla_material || {};
  const antic    = fam?.anticipos || {};

  const estiloFecha = {
    padding: '4px 8px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border-light)', background: 'var(--bg-2)',
    color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)',
  };

  const diasDelRango = () => {
    const a = new Date(desde + 'T00:00:00'), b = new Date(hasta + 'T00:00:00');
    if (isNaN(a) || isNaN(b)) return 0;
    return Math.round((b - a) / 86400000) + 1;
  };

  const th = (txt, alinear) => (
    <th style={{
      textAlign: alinear || 'right', padding: '8px 12px', fontSize: 11,
      textTransform: 'uppercase', letterSpacing: '.04em',
      color: 'var(--text-3)', fontWeight: 600,
      borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap',
    }}>{txt}</th>
  );

  const num = (v, opts = {}) => (
    <td style={{
      textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13,
      padding: '9px 12px', borderBottom: '1px solid var(--border)',
      cursor: opts.onClick ? 'pointer' : 'default',
      color: !v ? 'var(--text-3)' : v < 0 ? 'var(--red)' : opts.fuerte ? 'var(--text)' : 'var(--text-2)',
      fontWeight: opts.fuerte ? 600 : 400,
      textDecoration: opts.onClick ? 'underline dotted var(--border-light)' : 'none',
    }} onClick={opts.onClick} title={opts.onClick ? 'Ver detalle' : ''}>
      {!v ? '—' : dinero(v)}
    </td>
  );

  const tarjeta = (titulo, valor, nota, color) => (
    <div style={{
      background: 'var(--bg-2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '14px 18px', flex: '1 1 200px',
    }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-3)' }}>{titulo}</div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 6, color: color || 'var(--text)' }}>{valor}</div>
      {nota && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{nota}</div>}
    </div>
  );

  const pestana = (id, txt) => (
    <button onClick={() => { setTab(id); setDrill(null); }}
      style={{
        padding: '7px 16px', fontSize: 13, cursor: 'pointer',
        fontFamily: 'var(--font)', border: 'none', background: 'transparent',
        color: tab === id ? 'var(--text)' : 'var(--text-3)',
        fontWeight: tab === id ? 600 : 400,
        borderBottom: tab === id ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: -1,
      }}>{txt}</button>
  );

  // La clave con la que un renglon entra al desglose: el nombre del vendedor,
  // o el motivo por el que no hay uno. Tiene que ser la MISMA cuenta que hace
  // el servidor, o el filtro y el subtotal no coincidirian.
  const claveVend = (l) => l.vendedor || l.vendedor_nota || 'SIN_PEDIDO';

  const lineasAgrupadas = () => {
    if (!drill || !drill.lines.length) return [];
    const porDoc = {};
    for (const l of drill.lines) {
      if (vendFiltro && claveVend(l) !== vendFiltro) continue;
      const k = l.document || ('#' + l.move_id);
      if (!porDoc[k]) porDoc[k] = { ...l, document: k, amount: 0, n: 0 };
      porDoc[k].amount += l.amount;
      porDoc[k].n += 1;
    }
    return Object.values(porDoc).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  };

  const chartMeses = meses.map((m) => ({
    name: mesCorto(m.month),
    Facturado: Math.round(m.billed_net),
    Libros: Math.round(m.book_net),
  }));

  const chartFam = familias
    .filter((f) => f.familia !== 'NO_INGRESO')
    .map((f) => ({ name: FAM_LABEL[f.familia] || f.familia, key: f.familia, Total: Math.round(f.total) }));

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Dashboard Ejecutivo</h1>
          <input type="date" value={desde} max={hasta}
            onChange={(e) => setDesde(e.target.value)} style={estiloFecha} />
          <span style={{ color: 'var(--text-3)', fontSize: 13 }}>a</span>
          <input type="date" value={hasta} min={desde}
            onChange={(e) => setHasta(e.target.value)} style={estiloFecha} />
          <button onClick={actualizar}
            style={{
              padding: '5px 12px', borderRadius: 'var(--radius)',
              border: '1px solid var(--border-light)', background: 'var(--bg-2)',
              color: 'var(--text-2)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)',
            }}>Actualizar</button>
          <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' }}>
            Odoo en vivo · consultado {horaLocal(data?.consulted_at)}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {PRESETS.map(([id, txt]) => {
            const [d, h] = rangoPreset(id);
            const act = d === desde && h === hasta;
            return (
              <button key={id} onClick={() => aplicarPreset(id)}
                style={{
                  padding: '3px 10px', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'var(--font)', borderRadius: 'var(--radius)',
                  border: '1px solid ' + (act ? 'var(--accent)' : 'var(--border-light)'),
                  background: act ? 'var(--accent-glow)' : 'transparent',
                  color: act ? 'var(--accent-dark)' : 'var(--text-2)',
                  fontWeight: act ? 600 : 400,
                }}>{txt}</button>
            );
          })}
          <span style={{ fontSize: 12, color: 'var(--text-3)', alignSelf: 'center', marginLeft: 6 }}>
            {diasDelRango()} dias
          </span>
        </div>

        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 18 }}>
          {pestana('ingreso', 'Ingreso')}
          {pestana('familias', 'Familias')}
          {pestana('comparativo', 'Comparativo')}
          {pestana('cartera', 'Cartera')}
        </div>

        {error && (
          <div style={{
            background: 'var(--red-bg)', border: '1px solid var(--red)',
            borderRadius: 'var(--radius)', padding: '10px 14px',
            color: 'var(--red)', fontSize: 13, marginBottom: 16,
          }}>{error}</div>
        )}

        {/* ---------------- INGRESO ---------------- */}
        {loading && <div className="loading-center" style={{ padding: 60 }}><div className="spinner" /></div>}

        {!loading && tab === 'ingreso' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '0 0 18px', maxWidth: 720 }}>
              La cifra operativa es <strong>facturado neto</strong>. La provision de devengo y el ajuste
              de obra se muestran aparte y nunca se suman en silencio. Cualquier cifra se puede abrir.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              {tarjeta('Facturado neto', dinero(totals.billed_net), 'Metrica operativa', 'var(--accent)')}
              {tarjeta('Provision de devengo', dinero(totals.accrual), 'No es venta del periodo')}
              {tarjeta('Ajuste de obra (RMD)', dinero(totals.billings_excess), 'Cuenta 4000202')}
              {tarjeta('Neto en libros', dinero(totals.book_net), 'Solo para conciliar', 'var(--text-3)')}
            </div>

            <div style={{
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: '16px 12px 8px', marginBottom: 20,
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 8px 10px' }}>
                Facturado neto contra neto en libros
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartMeses} style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    const i = e && e.activeTooltipIndex;
                    if (typeof i === 'number' && meses[i]) abrirDetalle(meses[i], 'invoiced');
                  }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false}
                         tickFormatter={(v) => Math.round(v / 1000) + 'K'} />
                  <Tooltip formatter={(v) => dinero(v)}
                    contentStyle={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="Facturado" fill="var(--accent)" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Libros" fill="var(--bronze)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', overflowX: 'auto', marginBottom: 20,
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {th('Mes', 'left')}{th('Facturas')}{th('Notas cr.')}{th('Facturado neto')}
                    {th('Provision')}{th('RMD')}{th('Neto en libros')}
                  </tr>
                </thead>
                <tbody>
                  {meses.map((m) => (
                    <tr key={m.month}>
                      <td style={{ padding: '9px 12px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                        {mesCorto(m.month)}
                      </td>
                      {num(m.invoiced, { onClick: () => abrirDetalle(m, 'invoiced') })}
                      {num(m.refunds, { onClick: () => abrirDetalle(m, 'refunds') })}
                      {num(m.billed_net, { fuerte: true, onClick: () => abrirDetalle(m, 'invoiced') })}
                      {num(m.accrual, { onClick: () => abrirDetalle(m, 'accrual') })}
                      {num(m.billings_excess, { onClick: () => abrirDetalle(m, 'billings_excess') })}
                      {num(m.book_net)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ---------------- FAMILIAS ---------------- */}
        {!loading && tab === 'familias' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '0 0 18px', maxWidth: 780 }}>
              La familia sale de la categoria del producto. Los anticipos no traen producto, pero cada uno
              cuelga de un SO y de ahi se resuelve — esa parte se muestra <strong>en columna aparte</strong>,
              porque es una atribucion nuestra, no un dato etiquetado en Odoo.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              {tarjeta('Fierro', (mezcla.pct_fierro || 0) + '%', dinero(mezcla.fierro), 'var(--bronze)')}
              {tarjeta('Aluminio', (mezcla.pct_aluminio || 0) + '%', dinero(mezcla.aluminio), 'var(--blue)')}
              {tarjeta('Anticipos resueltos',
                (antic.total ? Math.round((antic.resuelto / antic.total) * 100) : 100) + '%',
                dinero(antic.resuelto) + ' de ' + dinero(antic.total), 'var(--accent)')}
              {tarjeta('Sin resolver', dinero(antic.sin_resolver),
                antic.lineas + ' lineas / ' + antic.sos + ' SO',
                antic.sin_resolver ? 'var(--red)' : 'var(--text-3)')}
            </div>

            <div style={{
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: '16px 12px 8px', marginBottom: 20,
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 8px 10px' }}>
                Ingreso por familia
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartFam}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false}
                         tickFormatter={(v) => Math.round(v / 1000) + 'K'} />
                  <Tooltip formatter={(v) => dinero(v)}
                    contentStyle={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="Total" radius={[3, 3, 0, 0]}>
                    {chartFam.map((c) => <Cell key={c.key} fill={FAM_COLOR[c.key] || 'var(--accent)'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{
              background: 'var(--bg-2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', overflowX: 'auto', marginBottom: 20,
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {th('Familia', 'left')}{th('Directo')}{th('De anticipos')}{th('Total')}{th('%')}{th('SO', 'right')}
                  </tr>
                </thead>
                <tbody>
                  {familias.map((f) => (
                    <tr key={f.familia}>
                      <td style={{ padding: '9px 12px', fontSize: 13, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                          background: FAM_COLOR[f.familia] || 'var(--accent)', marginRight: 8,
                        }} />
                        {FAM_LABEL[f.familia] || f.familia}
                      </td>
                      {num(f.directo, { onClick: () => abrirDom('Directo - ' + (FAM_LABEL[f.familia] || f.familia), f.drill) })}
                      {num(f.de_anticipos)}
                      {num(f.total, { fuerte: true })}
                      <td style={{ textAlign: 'right', padding: '9px 12px', fontSize: 13, color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>
                        {f.pct}%
                      </td>
                      <td style={{ textAlign: 'right', padding: '9px 12px', fontSize: 12, color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
                        {f.sos && f.sos.length ? f.sos.length : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ---------------- CARTERA ----------------
            Se pinta sin esperar a loading ni al rango de fechas: el
            componente trae sus propios datos y la cartera es siempre al dia
            de hoy, asi que el selector de fechas de arriba no le aplica. */}
        {tab === 'cartera' && <CarteraTab />}

        {/* ---------------- COMPARATIVO ---------------- */}
        {tab === 'comparativo' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '0 0 18px', maxWidth: 780 }}>
              El mismo rango de fechas, tres anios seguidos. <strong>Periodo</strong> es exactamente el
              corte que elegiste; <strong>acumulado</strong> va del 1 de enero a la fecha de cierre, para
              que la comparacion sea contra el mismo punto del calendario.
            </p>

            {compLoad && <div className="loading-center" style={{ padding: 60 }}><div className="spinner" /></div>}

            {!compLoad && comp && (() => {
              const act = comp[0], ant = comp[1], ant2 = comp[2];
              const var1 = ant.total ? (act.total / ant.total - 1) : null;
              const varY = ant.ytd ? (act.ytd / ant.ytd - 1) : null;
              const varAlu = ant.aluminio ? (act.aluminio / ant.aluminio - 1) : null;
              const pctTxt = (v) => v === null ? '—' :
                (v >= 0 ? '+' : '') + Math.round(v * 1000) / 10 + '%';
              const colorVar = (v) => v === null ? 'var(--text-3)'
                : v >= 0 ? 'var(--green)' : 'var(--red)';

              const filaComp = (etiqueta, campo, esPct) => (
                <tr key={etiqueta}>
                  <td style={{ padding: '9px 12px', fontSize: 13, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    {etiqueta}
                  </td>
                  {comp.map((c) => (
                    <td key={c.anio} style={{
                      textAlign: 'right', padding: '9px 12px', fontSize: 13,
                      fontFamily: 'var(--font-mono)', borderBottom: '1px solid var(--border)',
                      color: 'var(--text-2)',
                    }}>{esPct ? (c[campo] || 0) + '%' : dinero(c[campo])}</td>
                  ))}
                  <td style={{
                    textAlign: 'right', padding: '9px 12px', fontSize: 13, fontWeight: 600,
                    fontFamily: 'var(--font-mono)', borderBottom: '1px solid var(--border)',
                    color: esPct ? 'var(--text-2)'
                      : colorVar(ant[campo] ? (act[campo] / ant[campo] - 1) : null),
                  }}>
                    {esPct ? ((act[campo] || 0) - (ant[campo] || 0) >= 0 ? '+' : '') +
                        (Math.round(((act[campo] || 0) - (ant[campo] || 0)) * 10) / 10) + ' pts'
                      : pctTxt(ant[campo] ? (act[campo] / ant[campo] - 1) : null)}
                  </td>
                </tr>
              );

              return (
                <>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                    {tarjeta('Periodo ' + act.anio, dinero(act.total),
                      pctTxt(var1) + ' vs ' + ant.anio, colorVar(var1))}
                    {tarjeta('Acumulado al corte', dinero(act.ytd),
                      pctTxt(varY) + ' vs ' + ant.anio, colorVar(varY))}
                    {tarjeta('Aluminio', (act.pct_alu || 0) + '%',
                      pctTxt(varAlu) + ' en monto', 'var(--blue)')}
                    {tarjeta('Fierro', dinero(act.fierro),
                      pctTxt(ant.fierro ? (act.fierro / ant.fierro - 1) : null) + ' vs ' + ant.anio,
                      'var(--bronze)')}
                  </div>

                  <div style={{
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '16px 12px 8px', marginBottom: 20,
                  }}>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', padding: '0 8px 10px' }}>
                      Fierro y aluminio en el mismo periodo, tres anios
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={[...comp].reverse().map((c) => ({
                        name: c.anio,
                        Fierro: Math.round(c.fierro),
                        Aluminio: Math.round(c.aluminio),
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false}
                               tickFormatter={(v) => Math.round(v / 1000) + 'K'} />
                        <Tooltip formatter={(v) => dinero(v)}
                          contentStyle={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="Fierro" fill="var(--bronze)" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="Aluminio" fill="var(--blue)" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{
                    background: 'var(--bg-2)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', overflowX: 'auto', marginBottom: 20,
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {th('Concepto', 'left')}
                          {comp.map((c) => th(c.anio))}
                          {th('Variacion')}
                        </tr>
                      </thead>
                      <tbody>
                        {filaComp('Facturado en el periodo', 'total')}
                        {filaComp('Acumulado al corte', 'ytd')}
                        {filaComp('Fierro', 'fierro')}
                        {filaComp('Aluminio', 'aluminio')}
                        {filaComp('Neutral', 'neutral')}
                        {filaComp('Aluminio, % de la mezcla', 'pct_alu', true)}
                      </tbody>
                    </table>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', padding: '8px 12px' }}>
                      Rangos: {comp.map((c) => c.anio + ' (' + c.desde + ' a ' + c.hasta + ')').join('  ·  ')}
                    </div>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {/* ---------------- DRILL-DOWN ---------------- */}
        {!loading && drill && (
          <div id="drill-panel" style={{
            background: 'var(--bg-2)', border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius)', padding: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{drill.etiqueta}</div>
              <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 10 }}>
                {/* Antes decia solo "N apuntes", con N el conteo COMPLETO, mientras
                    la tabla mostraba nada mas los primeros 200. La diferencia se
                    leia como efecto de agrupar por documento, no como un corte. */}
                {drill.cargados < drill.total
                  ? 'mostrando ' + drill.cargados + ' de ' + drill.total + ' apuntes'
                  : drill.total + ' apuntes'}
              </span>
              <button onClick={() => setDrill(null)}
                style={{
                  marginLeft: 'auto', border: '1px solid var(--border-light)',
                  background: 'transparent', borderRadius: 'var(--radius)',
                  padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-2)',
                }}>Cerrar</button>
            </div>

            {drill.loading && <div className="loading-center"><div className="spinner" /></div>}
            {drill.error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{drill.error}</div>}

            {/* --- DESGLOSE POR VENDEDOR ---
                Los montos salen del servidor, calculados sobre el periodo
                completo. Los botones solo filtran la tabla de abajo. */}
            {!drill.loading && !drill.error && drill.porVendedor
              && drill.porVendedor.filas && drill.porVendedor.filas.length > 0 && (
              <div style={{
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: '10px 12px', marginBottom: 14,
              }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-3)', marginBottom: 8 }}>
                  Por vendedor · periodo completo
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => setVendFiltro('')}
                    style={{
                      padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                      fontFamily: 'var(--font)', borderRadius: 'var(--radius)',
                      border: '1px solid ' + (!vendFiltro ? 'var(--accent)' : 'var(--border-light)'),
                      background: !vendFiltro ? 'var(--accent-glow)' : 'transparent',
                      color: !vendFiltro ? 'var(--accent-dark)' : 'var(--text-2)',
                      fontWeight: !vendFiltro ? 600 : 400,
                    }}>
                    Todos {dinero(drill.porVendedor.suma)}
                  </button>

                  {drill.porVendedor.filas.map((f) => {
                    const act = vendFiltro === f.clave;
                    const gris = NO_ES_PERSONA.indexOf(f.clave) !== -1;
                    return (
                      <button key={f.clave} onClick={() => setVendFiltro(act ? '' : f.clave)}
                        title={f.documentos + ' documentos'}
                        style={{
                          padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                          fontFamily: 'var(--font)', borderRadius: 'var(--radius)',
                          border: '1px solid ' + (act ? 'var(--accent)' : 'var(--border-light)'),
                          background: act ? 'var(--accent-glow)' : 'transparent',
                          color: act ? 'var(--accent-dark)' : gris ? 'var(--text-3)' : 'var(--text-2)',
                          fontWeight: act ? 600 : 400,
                          fontStyle: gris ? 'italic' : 'normal',
                        }}>
                        {f.etiqueta} {dinero(f.monto)}
                      </button>
                    );
                  })}
                </div>

                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 9, lineHeight: 1.5 }}>
                  El vendedor sale del pedido de Odoo. <strong>No es la base de comision:</strong> el
                  corte de comisiones encima aplica la asignacion de cuenta, el origen de cuenta propia
                  o de casa, y los ajustes capturados a mano, asi que estos montos no van a coincidir
                  con lo que se paga. Suma del desglose {dinero(drill.porVendedor.suma)} sobre{' '}
                  {drill.porVendedor.documentos} documentos: tiene que cuadrar contra la cifra que
                  abriste.
                </div>
              </div>
            )}

            {!drill.loading && !drill.error && drill.cargados < drill.total && (
              <div style={{
                border: '1px solid var(--orange, #b45309)', borderRadius: 'var(--radius)',
                padding: '9px 12px', marginBottom: 14, fontSize: 12,
                color: 'var(--text)', lineHeight: 1.5,
              }}>
                La tabla de abajo trae los {drill.cargados} apuntes mas recientes de {drill.total}, no
                el mes completo. Los montos por vendedor de arriba si son del periodo completo; los de
                la tabla, no. No sumes la tabla.
              </div>
            )}

            {!drill.loading && !drill.error && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{th('Documento', 'left')}{th('Fecha', 'left')}{th('Cliente', 'left')}{th('Vendedor', 'left')}{th('Importe')}{th('', 'left')}</tr>
                </thead>
                <tbody>
                  {lineasAgrupadas().map((l) => (
                    <tr key={l.document}>
                      <td style={{ padding: '7px 12px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                        {l.document}{l.n > 1 && <span style={{ color: 'var(--text-3)' }}> ({l.n})</span>}
                      </td>
                      <td style={{ padding: '7px 12px', fontSize: 12, color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>{l.date}</td>
                      <td style={{ padding: '7px 12px', fontSize: 12, color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>{l.customer || '—'}</td>
                      {/* Nunca en blanco: cuando no hay un vendedor unico se dice por que.
                          Una celda vacia se lee como cero. */}
                      <td style={{
                        padding: '7px 12px', fontSize: 12, borderBottom: '1px solid var(--border)',
                        whiteSpace: 'nowrap',
                        color: l.vendedor ? 'var(--text-2)' : 'var(--text-3)',
                        fontStyle: l.vendedor ? 'normal' : 'italic',
                      }} title={l.so ? 'Pedido ' + l.so : 'Sin pedido en Odoo'}>
                        {l.vendedor_texto || (l.vendedor || 'Sin vendedor')}
                      </td>
                      <td style={{
                        padding: '7px 12px', fontSize: 12, textAlign: 'right',
                        fontFamily: 'var(--font-mono)', borderBottom: '1px solid var(--border)',
                        color: l.amount < 0 ? 'var(--red)' : 'var(--text)',
                      }}>{dinero(l.amount)}</td>
                      <td style={{ padding: '7px 12px', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                        <a href={l.odoo_url} target="_blank" rel="noreferrer"
                           style={{ color: 'var(--accent)', textDecoration: 'none' }}>Odoo</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
